import { useMemo, useState, useEffect, useCallback } from "react"
import ErrorBoundary from "../../ErrorBoundary"

import { useNavigate } from "react-router-dom"

import {
	Switch,
	Tooltip,
	Typography,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
	TextField,
} from "@mui/material"

import { RoundedButton } from "../../../components/ui/roundButton"

import {
	MoneyOff as MoneyOffIcon,
	Business as BusinessIcon,
	TrendingUp as TrendingUpIcon,
	Groups as GroupsIcon,
	Discount as DiscountIcon,
	AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material"

import DefaultModal from "../DefaultModal/DefaultModal"

import { useSnackbar } from "../../../components/SnackbarContext"

import {
	activateSubscription,
	checkoutStripePlan,
	listStripePlans,
	updateEnterpriseDocument,
	changeStripePlan,
} from "../../../services/scout"

import {
	isTrial,
	isFreePlan,
	getPlanEndDate,
} from "../../../services/utils"
import { openSupportForm } from "../../../services/support"

import {
	formatBRL,
	isValidCPF,
	isValidCNPJ,
} from "../../../utils"

import dayjs from "dayjs"

import { loader } from "../../../lib/loader"
import { IMaskInput } from "react-imask"

const CPF_MASK = "000.000.000-00"
const CNPJ_MASK = "AA.AAA.AAA/AAAA-00"

const TRACKED_SCOUT_PLAN_SLUGS = new Set(['free', 'starter', 'growth'])

const SCOUT_PAYMENT_PLANS = [
	{
		slug: "free",
		name: "Free",
        description: "Experimente sem compromisso",
		icon: <MoneyOffIcon />,
		rank: 1,
		price: 0,
		priceSuffix: "/mês",
		buttonLabel: "Assinar plano",
		features: [
            "1 número monitorado",
            "Histórico de 7 dias",
            "Registra texto e áudio",
            "Dashboard analítico",
            "Busca por termos",
            "1 administrador",
            "Conformidade LGPD",
            "Infraestrutura no Brasil",
            // "Histórico de 7 dias",
        ],
	},
	{
		slug: "starter",
		name: "Starter",
        description: "Para pequenos times",
		icon: <GroupsIcon />,
		rank: 2,
		price: 197,
		priceSuffix: "/mês",
		buttonLabel: "Assinar plano",
		features: [
            "Todos os recursos do Free",
            "5 números monitorados",
            "Histórico de 60 dias",
            "Registra todas as mídias",
            "Lista de exclusão",
            "Alertas por palavras-chave",
            "Exportação de conversas",
            "Suporte 8x5",
        ],
	},
	{
		slug: "growth",
		name: "Growth",
        description: "Equipes em operação real",
		icon: <TrendingUpIcon />,
		rank: 3,
		price: 447,
		priceSuffix: "/mês",
		buttonLabel: "Assinar plano",
		popular: true,
		badge: "Mais Popular",
		features: [
            "Todos os recursos do Starter",
            "20 números monitorados",
            "Histórico de 365 dias, sem limite de espaço de armazenamento",
            "IA e insights automáticos",
            "até 2 gestores",
            "Notificações por e-mail",
            "Gestão por equipes",
            "Exportação automática",
        ],
	},
	{
		slug: "enterprise",
		name: "Enterprise",
        description: "Regulação, integração e escala",
		icon: <BusinessIcon />,
		rank: 4,
		price: null,
		priceLabel: "Sob consulta",
		buttonLabel: "Saiba Mais",
		contactOnly: true,
		features: [
            "Todos os recursos do Growth",
            "Números sob medida",
            "Histórico ilimitado *",
            "Gestores ilimitados",
            "CRM nativo",
            "Onboarding assistido",
            "Suporte dedicado",
            "Gerente de conta dedicado",
            "SSO (Single Sign-On)",
        ],
	},
]

const getTrialStatus = (enterprise) => {
	if (!isTrial(enterprise)) {
		return null
	}

	const trialEndAt = dayjs(getPlanEndDate(enterprise))
	const today = dayjs().startOf('day')

	if (!trialEndAt.isValid()) {
		return null
	}

	const diffInDays = trialEndAt.startOf('day').diff(today, 'day')

	if (diffInDays < 0) {
		return {
			kind: 'expired',
			daysRemaining: 0,
			progress: 100,
		}
	}

	const daysRemaining = diffInDays
	const progress = Math.max(0, Math.min(100, ((7 - Math.min(daysRemaining, 7)) / 7) * 100))

	if (daysRemaining === 0) {
		return {
			kind: 'last-day',
			daysRemaining,
			progress: 100,
		}
	}

	if (daysRemaining === 1) {
		return {
			kind: 'last-day',
			daysRemaining,
			progress: Math.max(progress, 86),
		}
	}

	if (daysRemaining <= 2) {
		return {
			kind: 'critical',
			daysRemaining,
			progress: Math.max(progress, 86),
		}
	}

	if (daysRemaining <= 7) {
		return {
			kind: 'warning',
			daysRemaining,
			progress,
		}
	}

	return {
		kind: 'active',
		daysRemaining,
		progress,
	}
}

const slugifyPlanName = (value = '') => {
	return String(value)
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '')
}

const resolveScoutPlanSlug = (value = '') => {
	return slugifyPlanName(value).replace(/^scout-/, '')
}

const resolveCheckoutPlan = (plan, stripePlanBySlug) => {
	const planSlug = resolveScoutPlanSlug(plan?.slug || plan?.name || '')
	return stripePlanBySlug[planSlug] || plan || null
}

const canRenderUpgradePlan = (plan, currentPlanRank) => {
	return Boolean(plan?.contactOnly || plan?.rank > currentPlanRank)
}

const PlanPrice = ({ plan, annualDiscount }) => {
	if (typeof plan?.price === 'number') {
		const price = (annualDiscount)
			? formatBRL(plan.monthlyValue - plan.discountValue)
			: formatBRL(plan.monthlyValue || plan.price)
		return (
			<div className="scout-plan-card__price">
				<strong>{price}</strong>
				<span>{plan.priceSuffix || ''}</span>
			</div>
		)
	}

	return <div className="scout-plan-card__price">
		<strong>{plan?.priceLabel || 'Sob consulta'}</strong>
		<span></span>
	</div>
}

const PlansModal = ({ handleClose, enterprise, setEnterprise }) => {
	
	const { showSnackbar } = useSnackbar()

	const navigate = useNavigate()

	const [type, setType] = useState(
		(enterprise?.plan?.name.endsWith("Trial") || enterprise?.plan?.name.endsWith("Free"))
			? "trial-plans"
			: "upgrade-plans"
	)

	const [annualDiscount, setAnnualDiscount] = useState(true)
	const [stripePlanBySlug, setStripePlanBySlug] = useState({})
	const [showConfirmation, setShowConfirmation] = useState(null)
	const [pendingUpgradePlan, setPendingUpgradePlan] = useState(null)
	const [documentDialog, setDocumentDialog] = useState(null)
	const [documentType, setDocumentType] = useState("cpf")
	const [documentValue, setDocumentValue] = useState("")
	const [isChangingPlan, setIsChangingPlan] = useState(false)

	const mask = documentType === "cpf" ? CPF_MASK : CNPJ_MASK

	const trialStatus = useMemo(() => getTrialStatus(enterprise), [enterprise])
	const isTrialExpired = trialStatus?.kind === "expired"

	const subscription = useMemo(() => {
		const livePlanSlug = slugifyPlanName(enterprise?.plan?.name)
		const activePlanSlug = isTrial(enterprise) ? '' : livePlanSlug

		if (!enterprise)
			return

		return {
			activePlanSlug,
			isTrial: isTrial(enterprise),
			isFreePlan: isFreePlan(enterprise),
			scoutLicenses: enterprise.platforms?.scout_licenses ?? null,
			hasAnnualDiscout: Boolean(enterprise.external_coupon_id)
		}
	}, [enterprise])

	const currentSubscriptionPlan = useMemo(() => {
		if (!subscription?.activePlanSlug) {
			return null
		}

		const plan = SCOUT_PAYMENT_PLANS.find((plan) => resolveScoutPlanSlug(plan.slug || plan.name || '') === resolveScoutPlanSlug(subscription?.activePlanSlug)) || null

		return {
			...plan,
			price: (enterprise.external_monthly_price) ? enterprise.external_monthly_price : plan.price
		}
	}, [subscription?.activePlanSlug, enterprise])

	const modalPlans = useMemo(() => {
		const orderedPlans = [...SCOUT_PAYMENT_PLANS].sort((first, second) => Number(first.rank || 0) - Number(second.rank || 0))

		if (subscription && ! subscription.isTrial && ! subscription.isFreePlan) {
			setAnnualDiscount(subscription.hasAnnualDiscout)	
		}

		const normalizedPlans = orderedPlans.map((plan) => {
			return {
				...plan,
				...resolveCheckoutPlan(plan, stripePlanBySlug)
			}
		})

		const subscribedPlan = normalizedPlans.find((plan) => 
			subscription?.activePlanSlug.endsWith(plan.slug)
		)
		const subscribedPlanRank = subscribedPlan?.rank ?? 0
		const availableUpgradePlans = normalizedPlans.filter((plan) => canRenderUpgradePlan(plan, subscribedPlanRank))

		if (type !== "upgrade-plans" || !subscription?.activePlanSlug) {
			if (subscription && (subscription.isTrial || subscription.isFreePlan) && ! isTrialExpired)
				return normalizedPlans.filter((plan) => plan.slug !== "free")
			return normalizedPlans
		}

		const currentPlanSlug = resolveScoutPlanSlug(subscription?.activePlanSlug || '')
		const currentPlan = availableUpgradePlans.find((plan) => resolveScoutPlanSlug(plan.slug || plan.name || '') === currentPlanSlug)

		if (!currentPlan) {
			return availableUpgradePlans
		}

		return availableUpgradePlans.filter((plan) => Number(plan.rank || 0) > Number(currentPlan.rank || 0))
	}, [type, stripePlanBySlug, subscription, isTrialExpired])

	const handleCheckoutPlan = async (plan, skipDocument=false, withAnnualDiscount=false) => {
		if (!plan) {
			return
		}

		if (plan?.contactOnly) {
			openSupportForm()
			return
		}
		
		const selectedPlan = resolveCheckoutPlan(plan, stripePlanBySlug)
		const planId = selectedPlan?.id

		if (selectedPlan?.slug === "free") {
			setShowConfirmation({
				subtitle: "Tem certeza que deseja ativar o plano Free?",
				action: async () => {
					try {
						loader.on()
						await activateSubscription(planId)
					    const url = new URL("/home", window.location.origin)
						url.searchParams.set("greeting", "activation_success")
						window.location.replace(url.toString())
					} catch (nextError) {
						console.error(nextError)
						showSnackbar({
							message: "Falha ao ativar o plano Free. Tente novamente ou contate nosso time de suporte.",
							severity: "error"
						})
					} finally {
						loader.off()
					}
				}
			})
			return
		}

		if (!planId) {
			console.warn('Não foi possível determinar planId para checkout', plan)
			showSnackbar({ message: 'Não foi possível determinar o plano para pagamento. Tente novamente ou contate o nosso suporte.', severity: 'warning' })
			return
		}

		if (!isFreePlan(enterprise) && type === 'upgrade-plans') {
			setPendingUpgradePlan({
				...plan,
				price: (withAnnualDiscount) ? plan.monthlyValue - plan.discountValue : plan.monthlyValue
			})
			setType("confirm-upgrade")
			return
		}

		if (!enterprise?.cnpj && !skipDocument) {
			setDocumentDialog({ open: true, plan })
			return
		}

		try {
			loader.on()
			const response = await checkoutStripePlan({
				planId,
				checkoutMode: type === 'upgrade-plans' ? 'upgrade' : 'subscribe',
				withAnnualDiscount: withAnnualDiscount
			})
			loader.off()
			const redirectUrl = response?.url || response?.checkout_url || response?.session_url || response?.redirect_url
			if (redirectUrl) {
				window.location.assign(redirectUrl)
			}
		} catch (error) {
			console.error(error)
			loader.off()
			if (error?.message?.toLowerCase().includes('autenticado') || error?.message?.includes('401')) {
				navigate('/login')
			} else {
				showSnackbar({ message: 'Não foi possível iniciar o checkout. Tente novamente em alguns instantes.', severity: 'error' })
			}
		}
	}

	const isInvalidDocument = () => {
	  if (!documentValue)
	  	return true

	  if (documentType === "cpf")
	    return !isValidCPF(documentValue)

	  if (documentType === "cnpj")
	    return !isValidCNPJ(documentValue)

	  return true
	}

	const handleConfirmPlanChange = useCallback(async (withAnnualDiscount=false) => {
		const planId = pendingUpgradePlan?.planId || pendingUpgradePlan?.id || null

		if (!planId || isChangingPlan) {
			return
		}

		setIsChangingPlan(true)

		try {
			loader.on()
			await changeStripePlan({ planId, withAnnualDiscount })
			const url = new URL("/home", window.location.origin)
			url.searchParams.set("greeting", "upgrade_success")
			window.location.replace(url.toString())
		} catch (error) {
			console.error(error)
			if (error?.message?.toLowerCase().includes("autenticado") || error?.message?.includes("401")) {
				navigate("/login")
				return
			}

			showSnackbar({ message: "Não foi possível alterar o plano agora. Tente novamente em alguns instantes.", severity: "error" })
			setIsChangingPlan(false)
		}
	}, [isChangingPlan, navigate, pendingUpgradePlan, showSnackbar])

	useEffect(() => {
		let isMounted = true

		const loadStripePlans = async () => {
			try {
				const loadedPlans = await listStripePlans()
				if (!isMounted || !Array.isArray(loadedPlans)) {
					return
				}

				const nextPlanIds = loadedPlans.reduce((lookup, stripePlan) => {
					const slug = resolveScoutPlanSlug(stripePlan?.name)

					if (!TRACKED_SCOUT_PLAN_SLUGS.has(slug) || !stripePlan?.id) {
						return lookup
					}

					lookup[slug] = {
						id: stripePlan.id,
						slug: slug,
						monthlyValue: stripePlan.monthly_value,
						discountValue: stripePlan.discount_value,
						discountBadge: stripePlan.discount_badge,
					}
					return lookup
				}, {})

				setStripePlanBySlug(nextPlanIds)
			} catch (err) {
				console.warn('Falha ao carregar planos Stripe', err)
			}
		}

		loadStripePlans()

		return () => {
			isMounted = false
		}
	}, [])

	return (
		<>
			{documentDialog?.open && 
				<DefaultModal
					handleClose={() => {
						setDocumentDialog(null)
						setDocumentValue("")
					}}
					title="Informações adicionais"
					maxWidth="xs"
					subtitle="Nos informe abaixo o número do documento para emissão da Nota Fiscal."
					actionAlign="right"
					action={
						<div style={{ display: "flex", gap: 12 }}>
							<RoundedButton rounded="outlined" onClick={() => {
								setDocumentDialog(null)
								setDocumentValue("")
							}} sx={{ flex: 1 }}>
								Cancelar
							</RoundedButton>
							<RoundedButton onClick={async () => {
								await updateEnterpriseDocument(enterprise?.id, documentType, documentValue)
								setEnterprise((prev) => ({
									...prev,
									cnpj: documentValue,
								}))
								const plan = documentDialog?.plan
								setDocumentDialog(null)
								await handleCheckoutPlan(plan, true, annualDiscount)
							}} disabled={isInvalidDocument()} sx={{ flex: 1 }}>
								Salvar
							</RoundedButton>
						</div>
					}
				>
					<div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
						<FormControl fullWidth>
							<RadioGroup
								row
								value={documentType}
								onChange={(e) => {
									setDocumentType(e.target.value)
									setDocumentValue("")
								}}
							>
								<FormControlLabel value="cpf" control={<Radio />} label="CPF" />
								<FormControlLabel value="cnpj" control={<Radio />} label="CNPJ" />
							</RadioGroup>
						</FormControl>
						<TextField
							fullWidth
							size="small"
							label={documentType === "cpf" ? "CPF" : "CNPJ"}
							placeholder={documentType === "cpf" ? CPF_MASK : CNPJ_MASK}
							value={documentValue}
							onChange={(e) => setDocumentValue(e.target.value.toUpperCase())}
							InputProps={{
								inputComponent: IMaskInput,
								inputProps: {
									mask: mask,
									definitions: {
										"0": /[0-9]/,
										"A": /[a-zA-Z0-9]/,
									},
									unmask: false,
								},
							}}
						/>
					</div>
				</DefaultModal>
			}
			{showConfirmation &&
				<DefaultModal
					handleClose={() => setShowConfirmation(false)}
					title={showConfirmation.title ?? "Confirmação"}
					subtitle={showConfirmation.subtitle}
					maxWidth="xs"
					action={
						<div style={{ display: "flex", gap: 12 }}>
							<RoundedButton rounded="outlined" onClick={() => setShowConfirmation(false)}>
								Não
							</RoundedButton>
							<RoundedButton onClick={showConfirmation.action}>
								Sim
							</RoundedButton>
						</div>
					}
				/>
			}
			{type === "confirm-upgrade" &&
				<DefaultModal
					handleClose={() => setType("upgrade-plans")}
					icon={<AttachMoneyIcon />}
					title="Confirmar upgrade"
					contentTitle={`Deseja fazer o upgrade para o plano ${pendingUpgradePlan.name}?`}
					contentDescription={
						currentSubscriptionPlan?.price && pendingUpgradePlan?.price
							? `Sua mensalidade será alterada de ${formatBRL(currentSubscriptionPlan.price)} para ${formatBRL(pendingUpgradePlan.price)}.`
							: 'Seu plano será alterado imediatamente.'
					}
					action={(
						<button type="button" className="scout-primary-button scout-primary-button--home" onClick={() => handleConfirmPlanChange(annualDiscount)} disabled={isChangingPlan}>
							{isChangingPlan ? 'Alterando plano...' : 'Confirmar upgrade'}
						</button>
					)}
				/>
			}
			{type !== "confirm-upgrade" &&
				<DefaultModal
					handleClose={handleClose}
					maxWidth={type === "trial-plans" || type === "upgrade-plans" ? "md" : "sm"}
					paperClassName={type === "trial-plans" || type === "upgrade-plans" ? "default-modal__paper--wide" : ""}
					title={type === "upgrade-plans" ? "Faça o upgrade do seu plano" : "Assine o Kwik Scout"}
					subtitle="Conheça nossos planos"
					icon={null}
					actionClassName={type === "trial-plans" || type === "upgrade-plans" ? "default-modal__actions--end" : ""}
				>
					{modalPlans.length ? (
						<>
							{(isFreePlan(enterprise) || type !== "upgrade-plans") &&
								<div style={{ display: "flex", alignItems: "center" }}>
									<span>Mensal</span>
									<Switch
										checked={annualDiscount}
										onChange={(event) => setAnnualDiscount(event.target.checked)}
									/>
									<span>Anual (com desconto)</span>
								</div>
							}
							<div className={[
								'scout-payment-modal__body',
								'scout-plan-grid',
								modalPlans.length === 4 ? 'scout-plan-grid--quartet' : '',
							].filter(Boolean).join(' ')}>
								{modalPlans.map((plan) => (
									<article key={plan.slug} className={[
										'scout-plan-card',
										plan.popular ? 'scout-plan-card--popular' : '',
										modalPlans.length === 4 ? 'scout-plan-card--dense' : '',
									].filter(Boolean).join(' ')}>
										{plan.badge ? <span className="scout-plan-card__badge">{plan.badge}</span> : null}
										<h3>
											{plan.icon}
											{plan.name}
											{annualDiscount && plan.discountBadge && (
												<Tooltip title={plan.discountBadge}>
													<DiscountIcon color="info" />
												</Tooltip>
											)}
										</h3>
				                        <Typography variant="subtitle2" className="scout-plan-card__description">
				                            {plan.description}
				                        </Typography>
										<PlanPrice plan={plan} annualDiscount={annualDiscount} />
										<ul className="scout-plan-card__features">
											{(plan.features || []).map((feature) => (
												<li key={`${plan.slug}-${feature}`}>
													<span className="compare-yes">✔</span>
													<span>{feature}</span>
												</li>
											))}
										</ul>
										<button
											type="button"
											className={plan.popular ? 'scout-plan-card__button scout-plan-card__button--primary' : 'scout-plan-card__button'}
											onClick={() => handleCheckoutPlan(plan, false, annualDiscount)}
										>
											{plan.buttonLabel || 'Assinar plano'}
										</button>
									</article>
								))}
							</div>
						</>
					) : (<div className="default-modal__empty-state">Não há planos acima do seu pacote atual.</div>)}
				</DefaultModal>
			}
		</>
	)
}

const PlansModalWithBoundary = (props) => (
    <ErrorBoundary
        componentName="PlansModal"
        showReload
        logToBackend
        title="Erro no upgrade de plano"
        message="Ocorreu um erro ao carregar os planos disponíveis. Tente novamente."
    >
        <PlansModal {...props} />
    </ErrorBoundary>
)

export default PlansModalWithBoundary
